import { useQuery } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { ResourceType } from '@cotera/api';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';

export type FolderAncestor = {
  id: string;
  name: string;
  scope: 'user' | 'org' | 'external';
  parentId: string | null;
};

/**
 * Depth guard. Folder nesting is shallow in practice; the cap exists so a cycle
 * introduced by a bad move can't spin this into an unbounded request loop.
 */
const MAX_ANCESTOR_DEPTH = 32;

function isResultLike(
  value: unknown
): value is { isOk: () => boolean; value: unknown } {
  if (value === null || value === undefined || typeof value !== 'object') {
    return false;
  }
  return (
    'isOk' in value &&
    typeof (value as { isOk?: unknown }).isOk === 'function' &&
    'value' in value
  );
}

/** Normalizes the `resource.get` folder response (Result-wrapped or plain). */
function extractFolder(data: unknown): FolderAncestor | null {
  if (data === null || data === undefined || typeof data !== 'object') {
    return null;
  }
  if (isResultLike(data)) {
    return data.isOk() ? extractFolder(data.value) : null;
  }
  const unwrapped =
    'type' in data &&
    (data as { type: string }).type === 'folder' &&
    'folder' in data
      ? (data as { folder: unknown }).folder
      : data;
  if (
    unwrapped === null ||
    typeof unwrapped !== 'object' ||
    !('id' in unwrapped) ||
    !('name' in unwrapped)
  ) {
    return null;
  }
  const folder = unwrapped as {
    id: string;
    name: string;
    scope?: 'user' | 'org' | 'external';
    parentId?: string | null;
  };
  return {
    id: folder.id,
    name: folder.name,
    scope: folder.scope ?? 'user',
    parentId: folder.parentId ?? null,
  };
}

/** The folder's own record plus its ancestor chain, as one cached unit. */
type FolderChain = {
  /** The folder itself, or `null` when it can't be read. */
  self: FolderAncestor | null;
  /** Ancestors, root-most first, excluding the folder itself. */
  chain: FolderAncestor[];
};

/**
 * Resolves a folder's full ancestor chain, root-most first, EXCLUDING the
 * folder itself — plus the folder's own record, which the walk has to read
 * anyway to find the first parent. Views nested under a folder (its chat
 * history) need the folder's name and scope for their trail and would otherwise
 * repeat that same request.
 *
 * The folder contents endpoint returns only `{name, scope}` for the folder being
 * viewed, which forced breadcrumbs to render a flat `Library › Current` no
 * matter how deep the folder actually sat — intermediate parents were neither
 * visible nor clickable. `resource.get` does return `parentId`, so the chain is
 * recoverable by walking up one hop at a time.
 *
 * Each hop reads through `fetchQuery` on the shared per-resource key, so hops
 * are shared with (and warmed by) every other folder query in the app, and
 * revisiting a sibling folder resolves from cache without new requests.
 */
export function useFolderAncestors(folderId: string | null | undefined): {
  ancestors: FolderAncestor[];
  /** The folder itself — `null` while loading, or when it can't be read. */
  folder: FolderAncestor | null;
  isLoading: boolean;
} {
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);
  const enabled =
    folderId !== null && folderId !== undefined && folderId !== '';

  const { data, isLoading } = useQuery({
    queryKey: [orgId, 'folder-ancestors', folderId ?? ''],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<FolderChain> => {
      if (folderId === null || folderId === undefined) {
        return { self: null, chain: [] };
      }
      const chain: FolderAncestor[] = [];
      const seen = new Set<string>([folderId]);
      let cursor: string | null = null;

      const self = extractFolder(
        await queryClient.fetchQuery({
          queryKey: queryKeys.resource.get({
            orgId,
            type: ResourceType.Folder,
            id: folderId,
          }),
          staleTime: 60_000,
          queryFn: () =>
            client.resource.get({ type: ResourceType.Folder, id: folderId }),
        })
      );
      cursor = self?.parentId ?? null;

      while (cursor !== null && chain.length < MAX_ANCESTOR_DEPTH) {
        if (seen.has(cursor)) {
          break;
        }
        seen.add(cursor);
        const parentId: string = cursor;
        const parent = extractFolder(
          await queryClient.fetchQuery({
            queryKey: queryKeys.resource.get({
              orgId,
              type: ResourceType.Folder,
              id: parentId,
            }),
            staleTime: 60_000,
            queryFn: () =>
              client.resource.get({ type: ResourceType.Folder, id: parentId }),
          })
        );
        if (parent === null) {
          // An ancestor we can't read (revoked access mid-chain). Stop rather
          // than rendering a trail with a hole in it.
          break;
        }
        chain.push(parent);
        cursor = parent.parentId;
      }

      return { self, chain: chain.reverse() };
    },
  });

  return {
    ancestors: data?.chain ?? [],
    folder: data?.self ?? null,
    isLoading: enabled && isLoading,
  };
}
